// NOTE: the code commented out is required in the next sprint, removed as api is not ready

import { Col, Flex, message, Row } from 'antd';
import { useEffect, useMemo } from 'react';

import HeaderCard from 'components/HeaderCard';
import MultiSelect from 'components/MultiSelect';
import SearchBox from 'components/SearchBox';
import SelectComponent from 'components/Select';
import CollectionWidgetContainer from './components/CollectionWidgetContainer';
import FarmerSearch from './components/FarmerSearch';
import HotcashTable from './components/HotcashTable';
import HotcashWidget from './components/activity-widgets/HotCashWidget';
import LateAgronomistWidget from './components/activity-widgets/LateAgronomistWidget';
import LateCashWidget from './components/activity-widgets/LateCashWidget';
import TodaysActivityWidget from './components/activity-widgets/TodaysActivityWidget';

import useCountryAndSeasonIndexFilter from '../../../hooks/useCountryAndSeasonIndexFilter';
import useCashActivity from './hooks/useCashActivity';
import useCashTableFilters, {
  CollectionStatus,
} from './hooks/useCashTableFilters';
import useCollectionStats from './hooks/useCollectionStats';
import usePaymentRecords from './hooks/usePaymentRecords';

import testId from 'utils/testId';

import RBCountryFilter from 'components/UniversalFilters/RBCountryFilter';
import { useSearchParams } from 'react-router-dom';
import { ERRORS } from 'utils/constants';
import './style.scss';

const pageConfig = {
  header: 'Cash Acknowledgement',
  description:
    'View, Monitor and track cash held by agronomists and easily spot any issues.',
};

function Hotcash() {
  const [messageApi, contextHolder] = message.useMessage({ maxCount: 1 });

  const [searchParams] = useSearchParams();

  const invoiceId = searchParams.get('invoiceId');

  const {
    currentCountry,
    currentSeasonIdx,
    setCurrentCountry,
    setCurrentSeasonIdx,
    seasonIndexFilterOptions,
    isLoadingSeasonIndexFilter,
  } = useCountryAndSeasonIndexFilter();

  const {
    currentCollectionStatus,
    setCurrentCollectionStatus,
    collectionStatusFilterOptions,
    currentAgronomist,
    setCurrentAgronomist,
    agronomistFilterOptions,
    isAgronomistListLoading,
    resetTableFilters,
    currentSearchedInvoice,
    setCurrentSearchedInvoice,
    currentInvoice,
    setCurrentInvoice,
    currentFarmer,
    setCurrentFarmer,
  } = useCashTableFilters(currentCountry);

  const filterParams = useMemo(() => {
    const seasonIdx =
      (currentSeasonIdx as string)?.trim()?.length > 0
        ? currentSeasonIdx
        : null;

    const params: Record<string, string> = {};

    const status = currentCollectionStatus?.toUpperCase();

    if (status === 'LATE') {
      params['expiredAt.lessThan'] = new Date().toISOString();
      params['status.equals'] = 'RECEIVED';
    }

    if (status === 'SETTLED') {
      params['status.equals'] = 'DEPOSITED';
    }

    if (status === 'LIVE') {
      params['status.equals'] = 'RECEIVED';
      params['expiredAt.greaterThan'] = new Date().toISOString();
    }

    const invoice =
      (currentInvoice as string)?.trim()?.length > 0 ? currentInvoice : null;
    const country =
      currentCountry?.trim()?.length > 0 ? currentCountry : 'Indonesia';
    const farmer = currentFarmer?.length > 0 ? currentFarmer?.join(',') : null;

    return {
      ...(invoice && { 'description.contains': invoice }),
      ...(currentAgronomist?.length > 0 && {
        'agId.in': currentAgronomist?.join(','),
      }),
      ...(farmer && { 'farmerId.in': farmer }),
      'country.equals': country,
      projection: 'collection-payment-season-list-dto',
      ...params,
      ...(seasonIdx && { 'seasonTypeId.equals': seasonIdx }),
    };
  }, [
    currentAgronomist,
    currentCollectionStatus,
    currentCountry,
    currentInvoice,
    currentSeasonIdx,
    currentFarmer,
  ]);

  const {
    isLoading: isPaymentRecordsLoading,
    data: paymentRecords,
    total,
    handlePageChange,
    currentPage,
    pageSize,
    setData: setPaymentRecordsData,
    error: paymentRecordsError,
  } = usePaymentRecords(filterParams);

  const {
    data: collectionStats,
    isLoading: isCollectionStatsLoading,
    error: collectionStatsError,
  } = useCollectionStats(currentCountry, currentSeasonIdx as string);

  const {
    data: activity,
    isLoading: isActivityLoading,
    error: activityError,
  } = useCashActivity(currentCountry);

  const handleSeasonIndexChange = (season: string) => {
    setCurrentSeasonIdx(season);
    resetTableFilters();
  };

  const handleCountryChange = (country: string) => {
    setCurrentCountry(country);
    setCurrentSeasonIdx(null);
    resetTableFilters();
  };

  const handleViewAllLateAgs = () => {
    messageApi.open({
      type: 'info',
      content: 'Filter applied',
      className: 'mt-5 d-flex justify-content-center',
    });
    setCurrentCollectionStatus(CollectionStatus.LATE);
  };

  const renderTopFilters = () => {
    return (
      <Flex gap={20} align="center" wrap="wrap">
        <RBCountryFilter
          selectedCountry={currentCountry}
          onChange={handleCountryChange}
        />
        <SelectComponent
          label="Season"
          placeholder={'Select Season'}
          options={seasonIndexFilterOptions}
          value={currentSeasonIdx}
          onChange={handleSeasonIndexChange}
          disabled={isLoadingSeasonIndexFilter}
        />
      </Flex>
    );
  };

  useEffect(() => {
    // set page back to 1 when filters change
    handlePageChange(1, pageSize);
  }, [
    currentCollectionStatus,
    handlePageChange,
    pageSize,
    currentAgronomist,
    currentFarmer,
    currentInvoice,
  ]);

  useEffect(() => {
    if (paymentRecordsError || collectionStatsError || activityError) {
      const content =
        paymentRecordsError || collectionStatsError || activityError;
      const type = content === ERRORS.EMPTY_DATA ? 'info' : 'error';
      messageApi.open({
        type,
        content,
        className: 'mt-5 d-flex justify-content-center',
      });
    }
  }, [collectionStatsError, paymentRecordsError, activityError]);

  useEffect(() => {
    if (invoiceId) {
      setCurrentInvoice(invoiceId);
      setCurrentSearchedInvoice(invoiceId);
    }
  }, [invoiceId]);

  return (
    <Flex vertical className="hotcash-container">
      {contextHolder}

      <HeaderCard
        headerLabel={pageConfig.header}
        headerInfo={pageConfig.description}
        filters={renderTopFilters()}
      />

      <CollectionWidgetContainer
        slot={{
          hotcash: (
            <HotcashWidget
              isLoading={isCollectionStatsLoading || isLoadingSeasonIndexFilter}
              amount={collectionStats?.totalCash?.amount ?? 0}
              currency={collectionStats?.totalCash?.currency ?? 'IDR'}
            />
          ),
          lateCash: (
            <LateCashWidget
              isLoading={isCollectionStatsLoading || isLoadingSeasonIndexFilter}
              amount={collectionStats?.lateCash?.amount ?? 0}
              currency={collectionStats?.lateCash?.currency ?? 'IDR'}
            />
          ),
          lateAgronomist: (
            <LateAgronomistWidget
              isLoading={isCollectionStatsLoading || isLoadingSeasonIndexFilter}
              count={collectionStats?.lateAgronomist ?? 0}
              onViewAll={handleViewAllLateAgs}
            />
          ),
          todayActivity: (
            <TodaysActivityWidget
              activityList={activity?.cashActivities || []}
              isLoading={isActivityLoading}
            />
          ),
        }}
      />

      <Row>
        <Col span={24} className="hotcash-table-container">
          <Row className="filters">
            <SearchBox
              className="search-box invoice-search-box"
              disabled={false}
              label={`Invoice`}
              value={currentSearchedInvoice}
              placeholder={`Search Invoice`}
              onSearch={setCurrentInvoice}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setCurrentSearchedInvoice(e.target.value)
              }
              data-testid={testId.hotcashPage?.['inp-hotcash-invoice_search']}
            />
            <FarmerSearch
              country={currentCountry}
              selectedValues={currentFarmer}
              onSelect={(options) => setCurrentFarmer(options)}
              data-testid={testId.hotcashPage?.['inp-hotcash-farmer_search']}
            />

            <MultiSelect
              allowClear
              className="agronomist-select"
              defaultValue={null}
              disabled={isAgronomistListLoading}
              loading={isAgronomistListLoading}
              label="Agronomist"
              options={agronomistFilterOptions}
              value={currentAgronomist}
              onChange={setCurrentAgronomist}
              data-testid={testId.hotcashPage['slc-hotcash-agronomist']}
            />

            <SelectComponent
              allowClear
              defaultValue={null}
              disabled={false}
              loading={false}
              label="Status"
              options={collectionStatusFilterOptions}
              placeholder={'Select Status'}
              value={currentCollectionStatus}
              onChange={setCurrentCollectionStatus}
              data-testid={testId.hotcashPage['slc-hotcash-status']}
            />
          </Row>

          <HotcashTable
            country={currentCountry}
            data={paymentRecords || []}
            setData={setPaymentRecordsData}
            loading={isPaymentRecordsLoading}
            pagination={{
              total,
              current: currentPage,
              pageSize,
              onChange: handlePageChange,
              onShowSizeChange: handlePageChange,
            }}
          />
        </Col>
      </Row>
    </Flex>
  );
}

export default Hotcash;
